import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { productListApiService } from "../../../service/admin/productApiService";
import AlibabaAdminService from "../../../service/admin/alibabaAdminService";
import { adminApiService } from "../../../service/admin/adminApiService";
// import { useNavigate } from "react-router-dom";
import { categoryApiService } from "../../../service/admin/categoryApiService";
import { productPricingAlgorithm } from "../../../helper/utils";
import { SearchLogService } from "../../../service/admin/searchLogService";
import { fetchToken } from "../../../lib/globalApiClient";
import ExcelTable from "../../../component/library/excelTable";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import Logo from "../../../website/assets/images/common/logo_no_image.png";
import "./style.css";

export default function AddProduct() {
  // const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    page: 1,
    pageper: 10,
    method: "get-product-info",
    file: null,
    categoryId: "",
  });

  // Local state for image upload flow
  const [imageBase64, setImageBase64] = useState("");
  const [imageId, setImageId] = useState("");

  const [paginationInfo, setPaginationInfo] = useState({
    totalRecords: 0,
    totalPage: 1,
    currentPage: 1,
    pageSize: 10,
  });
  const [pageInput, setPageInput] = useState(1);

  const [productList, setProductList] = useState([]);
  const [selectedOfferIds, setSelectedOfferIds] = useState(new Set());
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  const [columnFilters, setColumnFilters] = useState({
    moqMin: "",
    moqMax: "",
    priceMin: "",
    priceMax: ""
  });

  const [categoryMap, setCategoryMap] = useState({});
  const [categoryFlatList, setCategoryFlatList] = useState([]);
  const [rootTokenIndex, setRootTokenIndex] = useState({});
  const [synonyms, setSynonyms] = useState({});
  const [pricingLoading, setPricingLoading] = useState(false);

  // Bulk SKU Excel upload state
  const [skuFile, setSkuFile] = useState(null);
  const [skuUploadJobId, setSkuUploadJobId] = useState(null);
  const [skuUploadProgress, setSkuUploadProgress] = useState(null);
  const [skuUploadUploading, setSkuUploadUploading] = useState(false);
  const skuPollRef = useRef(null);

  // Global categories from DB (main + subcategories) for Global Product Search
  const [globalCategoriesTree, setGlobalCategoriesTree] = useState([]);
  const [globalCategoriesLoading, setGlobalCategoriesLoading] = useState(true);
  const [selectedMainCategoryId, setSelectedMainCategoryId] = useState("");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
  const [categoryKeyword, setCategoryKeyword] = useState(""); // Required keyword for category search

  // Get current user ID for per-user table settings
  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  // Selected main category object (from DB)
  const selectedMainCategory = useMemo(
    () => globalCategoriesTree.find((c) => String(c._id) === String(selectedMainCategoryId)) || null,
    [globalCategoriesTree, selectedMainCategoryId]
  );
  // Subcategories of selected main
  const subcategoriesList = useMemo(
    () => (selectedMainCategory?.subcategories || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)),
    [selectedMainCategory]
  );
  const selectedSubcategory = useMemo(
    () => subcategoriesList.find((c) => String(c._id) === String(selectedSubcategoryId)) || null,
    [subcategoriesList, selectedSubcategoryId]
  );

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "file" ? files[0] : value,
    }));

    if (name === "method") {
      setImageBase64("");
      setImageId("");
      if (value !== "product-image-upload") {
        setFormData((prev) => ({ ...prev, file: null }));
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setColumnFilters(prev => ({ ...prev, [name]: value }));
  };

  const onMainCategoryChange = (e) => {
    const id = e.target.value;
    setSelectedMainCategoryId(id);
    setSelectedSubcategoryId("");
  };

  // Filters are sent with Search request (server-side across all pages). Table shows API response.

  const fetchProductList = async (overridePage) => {
    if (formData.method === "sku-excel-upload") return;
    setLoading(true);
    setError("");
    const pageToUse = overridePage !== undefined ? overridePage : formData.page;

    if (formData.method === "get-product-info" && !formData.title.trim()) {
      setError("Please enter a keyword to search.");
      setLoading(false);
      return;
    }

    if (formData.method === "product-by-image" && !formData.title.trim()) {
      setError("Please enter an image URL to search.");
      setLoading(false);
      return;
    }

    if (formData.method === "product-by-sku" && !formData.title.trim()) {
      setError("Please enter a SKU/OfferId to search.");
      setLoading(false);
      return;
    }

    if (formData.method === "product-by-categoryId") {
      if (!selectedMainCategory?.categoryId) {
        setError("Please select a main category.");
        setLoading(false);
        return;
      }
      if (!categoryKeyword.trim()) {
        setError("Please enter a keyword to search.");
        setLoading(false);
        return;
      }
    }

    if (formData.method === "product-by-sku") {
      try {
        const baseUrl = process.env.REACT_APP_APIUrl1688;
        const token = await fetchToken();
        const response = await fetch(baseUrl.replace(/\/$/, '') + '/product-detail-by-offerid', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
          body: JSON.stringify({ offerId: formData.title.trim(), country: "en" })
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const res = await response.json();
        let productData = res?.result?.result || res?.result || res?.data || res;

        if (productData && (productData.offerId || productData.id)) {
          let products = [normalizeProductData(productData, "product-by-sku")];
          if (products.length > 0) products = await applyPricingToProducts(products);
          setProductList(products);
          setPaginationInfo({ totalRecords: 1, totalPage: 1, currentPage: 1, pageSize: 1 });
          setError("");
        } else {
          setError("Product not found for the given SKU/OfferId.");
          setProductList([]);
        }
      } catch (err) {
        setError(`Error fetching product by SKU: ${err.message || 'Unknown error'}`);
        setProductList([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (formData.method === "product-image-upload") {
      try {
        if (!imageBase64) {
          setError("Please select an image to upload.");
          setLoading(false);
          return;
        }

        const safeParse = async (resp) => {
          try { return await resp.json(); } catch { return {}; }
        };

        const uploadOnce = async (payload) => {
          const baseUrl = process.env.REACT_APP_APIUrl1688;
          const token = await fetchToken();
          const r = await fetch(baseUrl.replace(/\/$/, '') + '/product-image-upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
            body: JSON.stringify(payload)
          });
          return await safeParse(r);
        };

        let uploadRes = await uploadOnce({ imageBase64 });
        let newImageId = uploadRes.result?.result;
        if (!newImageId) {
          const stripped = String(imageBase64).replace(/^data:[^,]+,/, '');
          uploadRes = await uploadOnce({ imageBase64: stripped });
          newImageId = uploadRes.result?.result;
        }
        if (!newImageId) throw new Error('Failed to get imageId from upload API');
        setImageId(newImageId);

        const baseUrl = process.env.REACT_APP_APIUrl1688;
        const token = await fetchToken();
        const searchResp = await fetch(baseUrl.replace(/\/$/, '') + '/product-image-id', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
          body: JSON.stringify({ imageId: newImageId })
        });
        const res = await safeParse(searchResp);
        const result = res?.result?.result || res?.result || res || {};
        let data = result?.data || result?.list || result?.items || [];

        if (newImageId) {
          SearchLogService.saveSearchKeyword(`img:${newImageId}`, 'global', 'image-search').catch(() => { });
        }

        if (Array.isArray(data) && data.length > 0) {
          data = data.map(p => normalizeProductData(p, formData.method));
          data = await applyPricingToProducts(data);
        }

        setProductList(Array.isArray(data) ? data : []);
        setPaginationInfo((prev) => ({
          ...prev,
          totalRecords: Array.isArray(data) ? data.length : 0,
          totalPage: 1,
          currentPage: 1,
          pageSize: Array.isArray(data) ? Math.max(10, data.length) : 10,
        }));
      } catch (err) {
        setError(err?.message || 'Failed to search by uploaded image.');
        setProductList([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    // API payload: MOQ and Price filters are applied client-side on response data
    let searchPayload = {
      beginPage: pageToUse,
      pageSize: formData.pageper,
      priceStart: "1",
      priceEnd: "10000000",
      country: "en",
      keywordTranslate: true,
      sort: "{\"price\":\"asc\"}",
    };

    const isKeywordOrCategory = formData.method === "get-product-info" || formData.method === "product-by-categoryId";
    if (formData.method === "get-product-info") {
      searchPayload.keyword = formData.title.trim() || undefined;
    } else if (formData.method === "product-by-image") {
      searchPayload = { beginPage: formData.page, pageSize: formData.pageper, country: "en", imageAddress: formData.title.trim() };
    } else if (formData.method === "product-by-categoryId") {
      const mainId = selectedMainCategory?.categoryId;
      const subId = selectedSubcategory?.categoryId;
      if (mainId) {
        searchPayload.categoryId = String(mainId);
        if (subId) searchPayload.categoryIdList = `${mainId},${subId}`;
      }
      searchPayload.keyword = categoryKeyword.trim();
    }
    const apiMethod = isKeywordOrCategory ? "get-product-info" : formData.method;

    productListApiService
      .searchProductList(JSON.stringify(searchPayload), apiMethod)
      .then(async (res) => {
        if (res?.result?.code === "200") {
          const result = res?.result?.result || {};
          let rawProducts = result.data || [];
          let products = rawProducts.map(p => normalizeProductData(p, formData.method));
          if (products.length > 0) products = await applyPricingToProducts(products);
          setProductList(products);
          setPaginationInfo({
            totalRecords: result.totalRecords,
            totalPage: result.totalPage,
            currentPage: result.currentPage,
            pageSize: result.pageSize,
          });
        } else {
          setError(`No products found. API Code: ${res?.result?.code}`);
          setProductList([]);
        }
      })
      .catch((err) => {
        const is429 = err?.status === 429 || (err?.message && String(err.message).includes("429"));
        const msg = is429
          ? "Too many requests (429). Please wait a minute and try again, or try changing the page later."
          : `Error fetching product data: ${err.message || "Unknown error"}`;
        setError(msg);
        setProductList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    setFormData((prev) => ({ ...prev, file: file || null }));
    if (!file) { setImageBase64(""); return; }
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setImageBase64(String(base64 || ''));
      setError("");
    } catch (err) {
      setError('Failed to read the selected image.');
      setImageBase64("");
    }
  };

  const getValueByPath = (obj, path) =>
    path.split(".").reduce((acc, part) => acc && acc[part], obj);

  useEffect(() => {
    setPageInput(formData.page);
    if (formData.method !== "sku-excel-upload") fetchProductList();
  }, [formData.page]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const method = params.get('method');
      const title = params.get('title') || '';
      if (!method) return;
      setFormData((prev) => ({ ...prev, method, title: method === 'product-by-image' ? title : prev.title }));
      if (method === 'product-by-image' && title) {
        setTimeout(() => fetchProductList(), 0);
      }
      if (method === 'product-image-upload') {
        const b64 = sessionStorage.getItem('alibaba_image_upload_b64');
        if (b64) {
          setImageBase64(b64);
          setTimeout(() => fetchProductList(), 50);
        }
      }
    } catch { }
  }, []);

  useEffect(() => {
    if (formData.method !== "sku-excel-upload" && (formData.method !== "product-image-upload" || imageBase64)) {
      fetchProductList();
    }
  }, [formData.pageper]);

  // Build category tree from admin Category API (fallback when globalCategory has no data)
  const mapExistingCategoryToTree = useCallback((nodes) => {
    if (!Array.isArray(nodes)) return [];
    return nodes
      .map((n) => ({
        _id: n._id ?? n.node_id ?? n.c_id,
        name: n.c_name || n.name || "Unnamed",
        categoryId: n.c_id ?? n.node_id ?? n._id,
        subcategories: mapExistingCategoryToTree(n.children || []),
      }))
      .filter((c) => c.categoryId != null || c.name);
  }, []);

  // Load categories for dropdown: globalCategory from DB first (main + subcategories), fallback to admin categories
  useEffect(() => {
    const loadCategoriesForDropdown = async () => {
      setGlobalCategoriesLoading(true);
      try {
        const res = await productListApiService.getGlobalCategories();
        const data = res?.data ?? res;
        const tree = Array.isArray(data) ? data : [];
        if (tree.length > 0) {
          setGlobalCategoriesTree(tree);
          setGlobalCategoriesLoading(false);
          return;
        }
      } catch (e2) {
        console.warn("Global categories API failed, falling back to admin categories:", e2?.message);
      }
      try {
        const res = await categoryApiService.getExistingCategory();
        const raw = res?.data ?? res;
        const data = Array.isArray(raw) ? raw : (raw && typeof raw === "object" && Array.isArray(raw.data) ? raw.data : raw && typeof raw === "object" && raw.children ? [raw] : []);
        const adminTree = mapExistingCategoryToTree(data);
        setGlobalCategoriesTree(adminTree);
      } catch (e) {
        console.warn("Admin categories load failed:", e?.message);
        setGlobalCategoriesTree([]);
      } finally {
        setGlobalCategoriesLoading(false);
      }
    };
    loadCategoriesForDropdown();
  }, [mapExistingCategoryToTree]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await categoryApiService.getExistingCategory();
        const data = res?.data || res;
        const flat = {};
        const list = [];
        const stem = (t) => t.replace(/(ing|ers|er|ies|es|s)$/i, (m) => (m === 'ies' ? 'y' : '')).replace(/[^a-z0-9]/g, '');
        const normalizeTokens = (name) =>
          String(name || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9\s]/g, " ").split(/\s+/).map(stem).filter((t) => t && t.length > 1);
        const walk = (nodes, path = "") => {
          (nodes || []).forEach((n) => {
            const nextPath = path ? `${path} > ${n.c_name}` : n.c_name;
            if (typeof n.c_id !== "undefined") flat[String(n.c_id)] = nextPath;
            if (typeof n.node_id !== "undefined") flat[String(n.node_id)] = nextPath;
            const nameTokens = normalizeTokens(n.c_name);
            const pathTokens = normalizeTokens(nextPath);
            const mergedTokens = new Set([...nameTokens, ...pathTokens]);
            list.push({ c_id: String(n.c_id ?? ""), node_id: String(n.node_id ?? ""), name: n.c_name || "", path: nextPath, tokens: mergedTokens });
            if (n.children && n.children.length) walk(n.children, nextPath);
          });
        };
        walk(data);
        setCategoryMap(flat);
        setCategoryFlatList(list);

        const roots = list.filter((c) => !(c.path || "").includes(" > "));
        const tokenIndex = {};
        const toTokens = (name) => String(name || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter((t) => t && t.length > 2);
        roots.forEach((r) => {
          const tokens = new Set(toTokens(r.name));
          if (tokens.has("home")) tokens.add("kitchen");
          if (tokens.has("beauty")) tokens.add("cosmetic");
          if (tokens.has("clothing")) tokens.add("apparel");
          if (tokens.has("electronics")) tokens.add("computer");
          tokens.forEach((tok) => { if (!tokenIndex[tok]) tokenIndex[tok] = r; });
        });
        setRootTokenIndex(tokenIndex);

        setSynonyms({
          home: ["household", "storage", "decor", "interior", "bedroom", "bathroom", "kitchen", "living", "cabinet"],
          kitchen: ["cookware", "utensil", "bottle", "cup", "mug", "jar", "spoon", "fork"],
          beauty: ["cosmetic", "skincare", "makeup"],
          clothing: ["apparel", "garment", "dress", "shirt", "pant", "jean"],
          electronics: ["computer", "phone", "mobile", "laptop", "camera", "usb", "charger"],
          toys: ["toy", "game"],
          jewelry: ["jewellery", "necklace", "ring", "bracelet"],
          sports: ["fitness", "outdoor", "martial", "boxing", "taekwondo", "protect", "gear", "shin", "guard"],
          storage: ["organizer", "organiser", "rack", "box", "cabinet", "holder", "container", "basket"],
          shoe: ["footwear", "sneaker", "boot", "sandals"],
        });
      } catch (e) {
        console.error("Category load failed", e);
      } finally {
        setInitialLoading(false);
      }
    };
    loadCategories();
  }, []);

  const normalizeProductData = (product, searchMethod) => {
    if (searchMethod === "product-by-sku") {
      const firstSku = product.productSkuInfos?.[0] || {};
      const saleInfo = product.productSaleInfo || {};
      const priceRange = saleInfo.priceRangeList?.[0] || {};

      const imageUrl = product.productImage?.images?.[0] || product.imageUrl || product.images?.[0] || product.mainImage || product.image || "";
      const price = firstSku.price || firstSku.consignPrice || priceRange.price || saleInfo.price || product.price || product.sp || product.vendor_price || 0;
      const consignPrice = firstSku.consignPrice || firstSku.price || priceRange.price || saleInfo.price || product.consignPrice || price || 0;
      const minOrderQuantity = firstSku.minOrderQuantity || product.minOrderQuantity || saleInfo.minOrderQuantity || product.quantity || 1;
      const monthSold = product.monthSold || saleInfo.monthSold || product.monthlySold || product.sold || 0;

      return {
        offerId: product.offerId || product.id || "",
        subjectTrans: product.subjectTrans || product.subject || product.title || "",
        minOrderQuantity: minOrderQuantity,
        monthSold: monthSold,
        imageUrl: imageUrl,
        priceInfo: { price: Number(price) || 0, consignPrice: Number(consignPrice) || 0 },
        promotionUrl: product.promotionUrl || product.pcDetailUrl || product.detailUrl || "",
        calculated_sp: product.calculated_sp,
        calculated_mrp: product.calculated_mrp,
        price_breakdown: product.price_breakdown,
        _raw: product
      };
    }

    return {
      ...product,
      offerId: product.offerId || product.id || "",
      subjectTrans: product.subjectTrans || product.subject || product.title || "",
      minOrderQuantity: product.minOrderQuantity || "",
      monthSold: product.monthSold || "",
      imageUrl: product.imageUrl || "",
      priceInfo: { price: product.priceInfo?.price || product.price || 0, consignPrice: product.priceInfo?.consignPrice || product.consignPrice || 0 },
      promotionUrl: product.promotionUrl || product.pcDetailUrl || "",
      calculated_sp: product.calculated_sp,
      calculated_mrp: product.calculated_mrp,
      price_breakdown: product.price_breakdown
    };
  };

  const sanitizeUrl = (u) => {
    if (!u || typeof u !== 'string') return '';
    let s = u.trim();
    if (!s) return '';
    if (s.startsWith('//')) s = 'https:' + s;
    if (s.startsWith('http:')) s = s.replace('http:', 'https:');
    try {
      const parsed = new URL(s);
      parsed.search = '';
      parsed.hash = '';
      return parsed.toString();
    } catch {
      return s.split('#')[0].split('?')[0];
    }
  };

  const getPromotionUrl = (item) => {
    if (!item) return '';
    const candidates = [item.promotionUrl, item.promotionURL, item.promotion_url, item.pcDetailUrl, item.pcDetailURL, item.detailUrl, item.detailURL, item.productUrl, item.productURL, item.productDetailUrl, item.webUrl, item.url].filter(Boolean);
    return sanitizeUrl(candidates[0] || '');
  };

  const isChecked = (offerId) => selectedOfferIds.has(String(offerId));
  const toggleOne = (offerId, checked) => {
    const id = String(offerId);
    setSelectedOfferIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const applyPricingToProducts = async (products) => {
    try {
      setPricingLoading(true);
      const needsPricing = products.filter(p => !p.calculated_sp);
      if (needsPricing.length === 0) return products;

      await Promise.all(products.map(async (product) => {
        if (!product.calculated_sp) {
          try {
            const pricingResult = await productPricingAlgorithm(
              product,
              product.detailed_info?.productSaleInfo,
              product.detailed_info?.productShippingInfo,
              product.detailed_info?.productSkuInfos
            );

            const calculatedProduct = pricingResult?.product ?? null;
            const calculatedSp = calculatedProduct?.finalPrice;
            if (calculatedSp && calculatedSp > 0) {
              product.calculated_sp = calculatedSp;
              product.calculated_mrp = calculatedSp * 2.5;
            } else {
              const productAp = getValueByPath(product, "priceInfo.consignPrice") || getValueByPath(product, "priceInfo.price") || 0;
              product.calculated_sp = productAp * 20;
              product.calculated_mrp = product.calculated_sp * 2.5;
              product.price_breakdown = { FBP: product.calculated_sp };
            }
          } catch (error) {
            const productAp = getValueByPath(product, "priceInfo.consignPrice") || getValueByPath(product, "priceInfo.price") || 0;
            product.calculated_sp = productAp * 20;
            product.calculated_mrp = product.calculated_sp * 2.5;
            product.price_breakdown = { FBP: product.calculated_sp };
          }
        }
      }));

      return products;
    } catch (error) {
      products.forEach(product => {
        if (!product.calculated_sp) {
          const productAp = getValueByPath(product, "priceInfo.consignPrice") || getValueByPath(product, "priceInfo.price") || 0;
          product.calculated_sp = productAp * 20;
          product.calculated_mrp = product.calculated_sp * 2.5;
          product.price_breakdown = { FBP: product.calculated_sp };
        }
      });
      return products;
    } finally {
      setPricingLoading(false);
    }
  };

  const importSelectedAsDrafts = async () => {
    try {
      if (selectedOfferIds.size === 0) {
        setError("Select at least one product.");
        return;
      }
      setImportLoading(true);
      let pick = filteredByMoqAndPrice.filter((p) => selectedOfferIds.has(String(p.offerId || p.id || "")));
      pick = await applyPricingToProducts(pick);

      const res = await AlibabaAdminService.importProductsAsDrafts(pick);
      const ok = res?.status?.code === 200 || res?.code === 200;
      if (ok) {
        setError("");
        if (window?.dispatchToast) {
          window.dispatchToast({ type: 'success', message: `Imported ${res?.data?.count || pick.length} products as drafts` });
        }
      } else {
        const msg = res?.error?.message || res?.status?.message || "Import failed";
        if (window?.dispatchToast) window.dispatchToast({ type: 'error', message: msg });
      }
    } catch (e) {
      if (window?.dispatchToast) window.dispatchToast({ type: 'error', message: e?.message || 'Unknown import error' });
    } finally {
      setImportLoading(false);
    }
  };

  // ---------- Bulk SKU Excel Upload ----------
  const handleSkuFileSelect = (e) => {
    const file = e.target.files?.[0] || null;
    setSkuFile(file);
    setSkuUploadProgress(null);
    setSkuUploadJobId(null);
    setError("");
  };

  const startSkuPolling = (jobId) => {
    if (skuPollRef.current) { clearTimeout(skuPollRef.current); skuPollRef.current = null; }
    let lastProcessed = -1;
    let unchangedPolls = 0;
    const poll = async () => {
      try {
        const res = await adminApiService.getSkuUploadProgress(jobId);
        const d = res?.data || res;
        if (d) {
          setSkuUploadProgress(d);
          if (d.status === 'completed' || d.status === 'failed' || d.status === 'cancelled') {
            skuPollRef.current = null;
            return;
          }
          // Safety stop: if backend has processed all rows but status lagged.
          if (Number(d.totalSkus) > 0 && Number(d.processed) >= Number(d.totalSkus)) {
            skuPollRef.current = null;
            return;
          }
          const changed = d.processed !== lastProcessed;
          lastProcessed = d.processed;
          unchangedPolls = changed ? 0 : unchangedPolls + 1;

          // For single-SKU upload, poll very lightly to avoid noisy repeated 304 calls.
          const isSingleSku = Number(d.totalSkus || 0) <= 1;
          let nextDelayMs;
          if (isSingleSku) {
            nextDelayMs = changed ? 4000 : 12000;
          } else {
            nextDelayMs = changed ? 3000 : Math.min(15000, 5000 + unchangedPolls * 2000);
          }
          skuPollRef.current = setTimeout(poll, nextDelayMs);
        } else {
          skuPollRef.current = setTimeout(poll, 10000);
        }
      } catch {
        skuPollRef.current = null;
      }
    };
    skuPollRef.current = setTimeout(poll, 2000);
  };

  useEffect(() => {
    return () => {
      if (skuPollRef.current) { clearTimeout(skuPollRef.current); skuPollRef.current = null; }
    };
  }, []);

  const handleSkuUpload = async () => {
    if (!skuFile) { setError("Please select an Excel file first."); return; }
    setSkuUploadUploading(true);
    setSkuUploadProgress(null);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", skuFile);
      const res = await adminApiService.uploadSkuListGlobalSearch(fd);
      const ok = res?.status?.code === 200 || res?.code === 200;
      const d = res?.data || res;
      if (ok && d?.jobId) {
        setSkuUploadJobId(d.jobId);
        setSkuUploadProgress({
          status: d.toProcess === 0 ? 'completed' : 'running',
          batchId: d.batchId ?? null,
          totalSkus: d.total,
          processed: d.skipped || 0,
          success: 0,
          failed: 0,
          skipped: d.skipped || 0,
        });
        if (d.toProcess > 0) startSkuPolling(d.jobId);
      } else {
        setError(res?.error?.message || res?.status?.message || "Upload failed");
      }
    } catch (e) {
      setError(e?.message || "Upload failed");
    } finally {
      setSkuUploadUploading(false);
    }
  };

  const handleCancelSkuJob = async () => {
    if (!skuUploadJobId) return;
    try {
      await adminApiService.cancelSkuJob(skuUploadJobId);
      if (window?.dispatchToast) window.dispatchToast({ type: 'info', message: 'Cancel requested' });
    } catch {
      if (window?.dispatchToast) window.dispatchToast({ type: 'error', message: 'Failed to cancel' });
    }
  };

  const resetSkuUpload = () => {
    setSkuFile(null);
    setSkuUploadJobId(null);
    setSkuUploadProgress(null);
    setSkuUploadUploading(false);
    if (skuPollRef.current) { clearTimeout(skuPollRef.current); skuPollRef.current = null; }
  };

  const clearFilters = () => {
    setColumnFilters({ moqMin: "", moqMax: "", priceMin: "", priceMax: "" });
  };

  // Filter API response by MOQ and Price (client-side)
  const filteredByMoqAndPrice = useMemo(() => {
    const list = productList || [];
    if (!list.length) return list;
    return list.filter((item) => {
      const moq = Number(item.minOrderQuantity) ?? 0;
      const price = Number(item.priceInfo?.price ?? item.priceInfo?.consignPrice ?? 0) ?? 0;
      const moqMin = columnFilters.moqMin ? Number(columnFilters.moqMin) : null;
      const moqMax = columnFilters.moqMax ? Number(columnFilters.moqMax) : null;
      const priceMin = columnFilters.priceMin ? Number(columnFilters.priceMin) : null;
      const priceMax = columnFilters.priceMax ? Number(columnFilters.priceMax) : null;
      if (moqMin != null && moq < moqMin) return false;
      if (moqMax != null && moq > moqMax) return false;
      if (priceMin != null && price < priceMin) return false;
      if (priceMax != null && price > priceMax) return false;
      return true;
    });
  }, [productList, columnFilters.moqMin, columnFilters.moqMax, columnFilters.priceMin, columnFilters.priceMax]);

  // Table data for ExcelTable (filtered by MOQ/Price from response)
  const tableRows = useMemo(() => {
    return (filteredByMoqAndPrice || []).map((item, index) => ({
      _id: item.offerId || item.id || index,
      serialNumber: index + 1,
      raw: item,
      image: (item.imageUrl || '').replace(/^http:/, 'https:'),
      offerId: item.offerId || "-",
      title: item.subjectTrans || "-",
      moq: item.minOrderQuantity || 0,
      monthSold: item.monthSold || 0,
      price: item.priceInfo?.price || 0,
      consignPrice: item.priceInfo?.consignPrice || 0,
      sp: item.calculated_sp || 0,
      mrp: item.calculated_mrp || 0,
      promotionUrl: getPromotionUrl(item),
    }));
  }, [filteredByMoqAndPrice]);

  // ExcelTable columns
  const columns = useMemo(() => [
    {
      key: "serialNumber",
      label: "#",
      width: 40,
      render: (value) => <span className="gps-serial">{value}</span>,
    },
    {
      key: "image",
      label: "IMAGE",
      width: 70,
      render: (value) => (
        <div className="gps-image-cell">
          {value ? (
            <img src={value} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = Logo; }} />
          ) : (
            <div className="gps-no-image">-</div>
          )}
        </div>
      ),
    },
    {
      key: "offerId",
      label: "SKU",
      width: 130,
      render: (value) => <span className="gps-sku">{value}</span>,
    },
    {
      key: "title",
      label: "Product Name",
      width: 280,
      wrap: true,
      render: (value) => <span className="gps-name">{value}</span>,
    },
    {
      key: "moq",
      label: "MOQ",
      width: 70,
      render: (value) => <span className="gps-num">{value}</span>,
    },
    {
      key: "monthSold",
      label: "Monthly Sold",
      width: 100,
      render: (value) => <span className="gps-num">{value}</span>,
    },
    {
      key: "price",
      label: "Price (¥)",
      width: 90,
      render: (value) => <span className="gps-price">¥{Number(value || 0).toFixed(2)}</span>,
    },
    {
      key: "consignPrice",
      label: "Consign (¥)",
      width: 90,
      render: (value) => <span className="gps-price">¥{Number(value || 0).toFixed(2)}</span>,
    },
    {
      key: "sp",
      label: "SP (₹)",
      width: 90,
      render: (value) => <span className="gps-price-inr">{value ? `₹${Math.round(value)}` : '-'}</span>,
    },
    {
      key: "mrp",
      label: "MRP (₹)",
      width: 90,
      render: (value) => <span className="gps-price-inr">{value ? `₹${Math.round(value)}` : '-'}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      width: 120,
      render: (_, row) => (
        <div className="gps-actions">
          <button className="gps-action-link" onClick={(e) => { e.stopPropagation(); window.open(`/admin/alibaba/details?id=${row.offerId}`, '_blank'); }}>View</button>
          {/* {row.promotionUrl && (
            <a href={row.promotionUrl} target="_blank" rel="noreferrer" className="gps-action-link" onClick={(e) => e.stopPropagation()}>Global</a>
          )} */}
        </div>
      ),
    },
  ], []);

  // Handle selection change from ExcelTable
  const handleSelectionChange = (selectedRows) => {
    const newSelectedIds = new Set();
    selectedRows.forEach(row => {
      const id = String(row.offerId || row._id || "");
      if (id && id !== "-") newSelectedIds.add(id);
    });
    setSelectedOfferIds(newSelectedIds);
  };

  // Get selected data for ExcelTable
  const selectedTableData = useMemo(() => {
    return tableRows.filter(row => selectedOfferIds.has(String(row.offerId || row._id || "")));
  }, [tableRows, selectedOfferIds]);

  const searchMethods = [
    { value: "get-product-info", label: "Keyword" },
    { value: "product-by-categoryId", label: "Category" },
    { value: "product-by-sku", label: "SKU" },
    { value: "product-by-image", label: "Image URL" },
    { value: "product-image-upload", label: "Upload" },
    { value: "sku-excel-upload", label: "Bulk Upload" },
  ];

  const setMethod = (value) => {
    setFormData((prev) => ({ ...prev, method: value }));
    if (value === "product-image-upload") setFormData((p) => ({ ...p, file: null }));
    if (value !== "product-image-upload") setImageBase64("");
    if (value !== "sku-excel-upload") {
      setSkuFile(null);
      setSkuUploadJobId(null);
      setSkuUploadProgress(null);
      setSkuUploadUploading(false);
      if (skuPollRef.current) { clearTimeout(skuPollRef.current); skuPollRef.current = null; }
    }
  };

  // Toolbar right: results count, selected count, Import (table only)
  const toolbarRightActions = (
    <div className="gps-toolbar-right">
      <span className="gps-results-count">Results: {filteredByMoqAndPrice.length} of {paginationInfo.totalRecords}</span>
      <span className="gps-selected-count">Selected: <strong>{selectedOfferIds.size}</strong></span>
      <button className="gps-btn gps-btn-warning" onClick={importSelectedAsDrafts} disabled={importLoading || pricingLoading || selectedOfferIds.size === 0}>
        {importLoading ? "..." : `Import (${selectedOfferIds.size})`}
      </button>
    </div>
  );

  return (
    <div className="gps-page">
      {/* Single card: tabs + tab content + filters + search */}
      <div className="gps-main-card">
        <h2 className="gps-main-title">Global Product Search</h2>

        {/* Tabs */}
        <div className="gps-tabs">
          {searchMethods.map((m) => (
            <button
              key={m.value}
              type="button"
              className={`gps-tab ${formData.method === m.value ? "gps-tab-active" : ""}`}
              onClick={() => setMethod(m.value)}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* One row: tab content (left) + MOQ & Price filter (right) + Page + Search */}
        <div className="gps-main-body gps-main-body-row">
          <div className="gps-tab-content">
            {formData.method === "get-product-info" && (
              <div className="gps-tab-row">
                <label className="gps-tab-label">Keyword</label>
                <input type="text" className="gps-tab-input" name="title" placeholder="e.g. perfume, bat" value={formData.title} onChange={handleChange} onKeyDown={(e) => e.key === "Enter" && fetchProductList(1)} />
              </div>
            )}
            {formData.method === "product-by-categoryId" && (
              <div className="gps-category-row">
                <select className="gps-tab-select gps-category-select" value={String(selectedMainCategoryId)} onChange={onMainCategoryChange} disabled={globalCategoriesLoading}>
                  <option value="">{globalCategoriesLoading ? "Loading..." : "Main category"}</option>
                  {globalCategoriesTree.map((cat) => (
                    <option key={String(cat._id)} value={String(cat._id)}>{cat.name}</option>
                  ))}
                </select>
                <select className="gps-tab-select gps-category-select" value={String(selectedSubcategoryId)} onChange={(e) => setSelectedSubcategoryId(e.target.value)} disabled={!selectedMainCategoryId}>
                  <option value="">Subcategory</option>
                  {subcategoriesList.map((sub) => (
                    <option key={String(sub._id)} value={String(sub._id)}>{sub.name}</option>
                  ))}
                </select>
                <input type="text" className="gps-tab-input gps-category-keyword" name="categoryKeyword" placeholder="Keyword (required)" value={categoryKeyword} onChange={(e) => { setCategoryKeyword(e.target.value); setError(""); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!categoryKeyword.trim()) { setError("Please enter a keyword to search."); return; } fetchProductList(1); } }} />
              </div>
            )}
            {formData.method === "product-by-sku" && (
              <div className="gps-tab-row">
                <label className="gps-tab-label">SKU / Offer ID</label>
                <input type="text" className="gps-tab-input" name="title" placeholder="Enter SKU or offer ID" value={formData.title} onChange={handleChange} onKeyDown={(e) => e.key === "Enter" && fetchProductList(1)} />
              </div>
            )}
            {formData.method === "product-by-image" && (
              <div className="gps-tab-row">
                <label className="gps-tab-label">Image URL</label>
                <input type="text" className="gps-tab-input" name="title" placeholder="Paste image URL" value={formData.title} onChange={handleChange} />
              </div>
            )}
            {formData.method === "product-image-upload" && (
              <div className="gps-tab-row">
                <label className="gps-tab-label">Upload image</label>
                <input type="file" accept="image/*" className="gps-file-input" onChange={handleFileSelect} />
                {imageBase64 && <span className="gps-upload-status">✓ Ready</span>}
              </div>
            )}
            {formData.method === "sku-excel-upload" && (
              <div className="gps-bulk-upload-section">
                {!skuUploadProgress ? (
                  <div className="gps-bulk-upload-form">
                    <div className="gps-tab-row">
                      <label className="gps-tab-label">Excel File</label>
                      <input type="file" accept=".xlsx,.xls,.csv" className="gps-file-input" onChange={handleSkuFileSelect} />
                    </div>
                    {skuFile && (
                      <button type="button" className="gps-btn gps-btn-primary gps-upload-btn" onClick={handleSkuUpload} disabled={skuUploadUploading}>
                        {skuUploadUploading ? "Uploading..." : "Upload & Process"}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="gps-bulk-progress-section">
                    {skuUploadProgress.status === 'running' && (
                      <>
                        {skuUploadProgress.batchId && (
                          <p className="gps-batch-id" style={{ fontSize: 12, marginBottom: 8, fontFamily: 'ui-monospace, monospace', color: '#374151' }}>
                            Batch ID: {skuUploadProgress.batchId}
                          </p>
                        )}
                        <div className="gps-progress-header">
                          <span className="gps-progress-text">
                            Processing {skuUploadProgress.processed} / {skuUploadProgress.totalSkus}
                            {skuUploadProgress.totalSkus > 0 && ` (${Math.round((skuUploadProgress.processed / skuUploadProgress.totalSkus) * 100)}%)`}
                          </span>
                          <button type="button" className="gps-btn gps-btn-cancel" onClick={handleCancelSkuJob}>Cancel</button>
                        </div>
                        <div className="gps-progress-bar">
                          <div
                            className="gps-progress-fill"
                            style={{ width: `${skuUploadProgress.totalSkus > 0 ? (skuUploadProgress.processed / skuUploadProgress.totalSkus) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="gps-progress-details">
                          <span className="gps-stat gps-stat-success">Saved: {skuUploadProgress.success}</span>
                          <span className="gps-stat gps-stat-skipped">Already in DB: {skuUploadProgress.skipped}</span>
                          <span className="gps-stat gps-stat-failed">Failed: {skuUploadProgress.failed}</span>
                        </div>
                      </>
                    )}
                    {(skuUploadProgress.status === 'completed' || skuUploadProgress.status === 'failed' || skuUploadProgress.status === 'cancelled') && (
                      <div className="gps-summary-card">
                        {skuUploadProgress.batchId && (
                          <p className="gps-batch-id" style={{ fontSize: 12, marginBottom: 10, fontFamily: 'ui-monospace, monospace', color: '#374151' }}>
                            Batch ID: {skuUploadProgress.batchId}
                          </p>
                        )}
                        <h3 className="gps-summary-title">
                          {skuUploadProgress.status === 'completed' ? 'Upload Complete' : skuUploadProgress.status === 'cancelled' ? 'Upload Cancelled' : 'Upload Failed'}
                        </h3>
                        <div className="gps-summary-stats">
                          <div className="gps-summary-stat gps-summary-total">
                            <span className="gps-summary-num">{skuUploadProgress.totalSkus}</span>
                            <span className="gps-summary-label">Total SKUs</span>
                          </div>
                          <div className="gps-summary-stat gps-summary-success">
                            <span className="gps-summary-num">{skuUploadProgress.success}</span>
                            <span className="gps-summary-label">Newly Saved</span>
                          </div>
                          <div className="gps-summary-stat gps-summary-skipped">
                            <span className="gps-summary-num">{skuUploadProgress.skipped}</span>
                            <span className="gps-summary-label">Already in DB</span>
                          </div>
                          <div className="gps-summary-stat gps-summary-failed">
                            <span className="gps-summary-num">{skuUploadProgress.failed}</span>
                            <span className="gps-summary-label">Failed</span>
                          </div>
                        </div>
                        {skuUploadProgress.duration && (
                          <p className="gps-summary-duration">Completed in {Math.round(skuUploadProgress.duration)}s</p>
                        )}
                        {skuUploadProgress.errors && skuUploadProgress.errors.length > 0 && (
                          <details className="gps-summary-errors">
                            <summary>Failed SKUs ({skuUploadProgress.errors.length})</summary>
                            <ul>
                              {skuUploadProgress.errors.map((err, i) => (
                                <li key={i}><strong>{err.offerId}</strong>: {err.error}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                        <button type="button" className="gps-btn gps-btn-primary" onClick={resetSkuUpload} style={{ marginTop: 12 }}>
                          Upload Another File
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {formData.method !== "sku-excel-upload" && (
            <div className="gps-search-right">
              <div className="gps-filter-inline">
                <label className="gps-filter-label">MOQ</label>
                <input type="number" className="gps-filter-input" name="moqMin" value={columnFilters.moqMin} onChange={handleFilterChange} placeholder="Min" min={0} />
                <input type="number" className="gps-filter-input" name="moqMax" value={columnFilters.moqMax} onChange={handleFilterChange} placeholder="Max" min={0} />
              </div>
              <div className="gps-filter-inline">
                <label className="gps-filter-label">Price (¥)</label>
                <input type="number" className="gps-filter-input" name="priceMin" value={columnFilters.priceMin} onChange={handleFilterChange} placeholder="Min" min={0} />
                <input type="number" className="gps-filter-input" name="priceMax" value={columnFilters.priceMax} onChange={handleFilterChange} placeholder="Max" min={0} />
              </div>
              {(columnFilters.moqMin || columnFilters.moqMax || columnFilters.priceMin || columnFilters.priceMax) && (
                <button type="button" className="gps-btn gps-btn-clear" onClick={clearFilters}>Clear</button>
              )}
              <div className="gps-page-per-group">
                <span className="gps-page-label">Page</span>
                <input type="number" min={1} className="gps-page-input" value={formData.page} onChange={(e) => setFormData(p => ({ ...p, page: Math.max(1, Number(e.target.value) || 1) }))} />
                <span className="gps-page-label">per</span>
                <select className="gps-perpage-select" value={formData.pageper} onChange={(e) => setFormData(p => ({ ...p, pageper: Number(e.target.value), page: 1 }))}>
                  {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              {!(formData.method === "product-by-categoryId" && !categoryKeyword.trim()) && (
                <button type="button" className="gps-btn gps-btn-primary" onClick={() => fetchProductList(1)} disabled={loading}>
                  {loading ? "Searching..." : "Search"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {error && <div className="gps-error">{error}</div>}

      {/* Results table — hidden during bulk upload */}
      {formData.method !== "sku-excel-upload" && (
      <div className="gps-results-card">
        <ExcelTable
          isLoading={initialLoading || loading}
          loadingText={initialLoading ? "Loading..." : "Fetching products..."}
          data={tableRows}
          columns={columns}
          showToolbarExport={false}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={<span className="gps-table-title">Results</span>}
          toolbarRightActions={toolbarRightActions}
          onSelectionChange={handleSelectionChange}
          selectedData={selectedTableData}
          selectable={true}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title=""
          userId={currentUserId}
        />

        {(productList.length > 0 || paginationInfo.totalRecords > 0) && (
          <div className="gps-pagination-bar">
            <span className="gps-page-per-label">Per page</span>
            <select className="gps-perpage-select" value={formData.pageper} onChange={(e) => setFormData(p => ({ ...p, pageper: Number(e.target.value), page: 1 }))}>
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button type="button" className="gps-page-btn" disabled={paginationInfo.currentPage === 1} onClick={() => setFormData(p => ({ ...p, page: 1 }))}>First</button>
            <button type="button" className="gps-page-btn" disabled={paginationInfo.currentPage === 1} onClick={() => setFormData(p => ({ ...p, page: Math.max(1, p.page - 1) }))}>Prev</button>
            <span className="gps-page-info">Page {paginationInfo.currentPage} / {paginationInfo.totalPage}</span>
            <button type="button" className="gps-page-btn" disabled={paginationInfo.currentPage >= paginationInfo.totalPage} onClick={() => setFormData(p => ({ ...p, page: Math.min(paginationInfo.totalPage, p.page + 1) }))}>Next</button>
            <button type="button" className="gps-page-btn" disabled={paginationInfo.currentPage >= paginationInfo.totalPage} onClick={() => setFormData(p => ({ ...p, page: paginationInfo.totalPage }))}>Last</button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
